import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, MessageCircle, Send, Linkedin, Instagram, Facebook } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import WhatsAppFallback from '../components/WhatsAppFallback';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try calling us directly.');
      console.error('Contact form error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCall = () => {
    window.location.href = 'tel:+917757999805';
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/917757999805?text=Hello%2C%20I%20am%20interested%20in%20your%20pool%20services', '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1763479142678-8e29f4edb538?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBwb29sJTIwZGVzaWdufGVufDB8fHx8MTc3MDA1MjE3NXww&ixlib=rb-4.1.0&q=85"
            alt="Contact Us"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-navy-900/70"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Contact Us in Bangalore</h1>
          <p className="text-xl md:text-2xl mb-2">Let's Discuss Your Pool Project</p>
          <p className="text-lg text-aqua-300 font-semibold">Swimming Pool Construction & Services — Bangalore, Karnataka</p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-b from-white to-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-4xl font-bold text-navy-900 mb-6">Get in Touch</h2>
              <p className="text-xl text-gray-600 mb-8">
                Looking for expert pool builders in Bangalore? Whether it's swimming pool construction, renovation, waterproofing, AMC, or jacuzzi installation — reach out to Aquarist Pools today for a free consultation!
              </p>

              <div className="space-y-6 mb-8">
                <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-aqua-100 to-aqua-200 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-aqua-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-navy-900 mb-1">Phone</h3>
                      <a href="tel:+917757999805" className="text-aqua-600 hover:text-aqua-700 text-lg">
                        077579 99805
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-aqua-100 to-aqua-200 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-aqua-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-navy-900 mb-1">Email</h3>
                      <a href="mailto:aquarist.pools@gmail.com" className="text-aqua-600 hover:text-aqua-700 block">
                        aquarist.pools@gmail.com
                      </a>
                      <a href="mailto:admin@aquaristpools.com" className="text-aqua-600 hover:text-aqua-700 block">
                        admin@aquaristpools.com
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-aqua-100 to-aqua-200 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-aqua-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-navy-900 mb-1">Address</h3>
                      <p className="text-gray-600 mb-2">
                        Opposite to Accent Ashlar Apartment<br />
                        Chikkanagamangala, 2, Huskur Rd<br />
                        Electronic City, Bengaluru<br />
                        Karnataka 560099
                      </p>
                      <a
                        href="https://www.google.com/maps/search/?api=1https://www.google.com/maps/search/?api=1&query=Aquarist+Pools+and+Fitness&query_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmMquery=Aquarist+Pools+and+Fitnesshttps://www.google.com/maps/search/?api=1&query=Aquarist+Pools+and+Fitness&query_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmMquery_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmM"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#00AEEF] hover:text-[#0098d4] font-semibold text-sm transition-colors"
                      >
                        <MapPin className="w-4 h-4" /> Get Directions
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="flex gap-4 flex-wrap">
                <Button 
                  size="lg"
                  onClick={handleCall}
                  className="flex-1 bg-aqua-500 hover:bg-aqua-600 text-white py-6 text-lg transition-all duration-300"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </Button>
                <Button 
                  size="lg"
                  onClick={handleWhatsApp}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white py-6 text-lg transition-all duration-300"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  WhatsApp
                </Button>
                <Button 
                  size="lg"
                  data-testid="contact-directions-btn"
                  onClick={() => window.open('https://www.google.com/maps/search/?api=1https://www.google.com/maps/search/?api=1&query=Aquarist+Pools+and+Fitness&query_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmMquery=Aquarist+Pools+and+Fitnesshttps://www.google.com/maps/search/?api=1&query=Aquarist+Pools+and+Fitness&query_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmMquery_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmM', '_blank')}
                  className="flex-1 bg-[#003366] hover:bg-[#002244] text-white py-6 text-lg transition-all duration-300"
                >
                  <MapPin className="mr-2 h-5 w-5" />
                  Get Directions
                </Button>
              </div>

              {/* Social Media Icons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-navy-900 mb-4">Connect With Us</h3>
                <div className="flex gap-4">
                  <a 
                    href="https://www.linkedin.com/in/aquarist-pools-and-fitness-06ba832aa/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white hover:scale-110 transition-transform duration-300 shadow-lg"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="w-6 h-6" />
                  </a>
                  <a 
                    href="https://www.instagram.com/aquaristpools/?utm_source=qr&igsh=MWp6M3JodDd2a2x4aA%3D%3D"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-600 to-purple-600 flex items-center justify-center text-white hover:scale-110 transition-transform duration-300 shadow-lg"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                  <a 
                    href="https://www.facebook.com/people/Aquarist-Pools-and-Fitness/61555539641106/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white hover:scale-110 transition-transform duration-300 shadow-lg"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="border-0 shadow-2xl">
              <CardHeader className="bg-gradient-to-r from-aqua-500 to-aqua-600 text-white">
                <CardTitle className="text-2xl">Send Us a Message</CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+91 XXXXX XXXXX"
                      className="h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your project..."
                      rows={5}
                      className="resize-none"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={loading}
                    size="lg"
                    className="w-full bg-navy-900 hover:bg-navy-800 text-white py-6 text-lg transition-all duration-300"
                  >
                    {loading ? 'Sending...' : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                  <WhatsAppFallback
                    prefill={`Hi Aquarist Pools, I'd like to reach you. Name: ${formData.name || '[Name]'}, Phone: ${formData.phone || '[Phone]'}. ${formData.message ? 'Message: ' + formData.message : ''}`}
                    label="Or send this message via WhatsApp"
                  />
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Visit Us / Map Section */}
      <section className="py-20 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Visit Us</h2>
            <p className="text-xl text-gray-300">
              We're located in Electronic City, Bengaluru
            </p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-[#00AEEF]/20 flex items-center justify-center mx-auto mb-6">
              <MapPin className="w-10 h-10 text-[#00AEEF]" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Aquarist Pools and Fitness</h3>
            <p className="text-gray-300 mb-2 text-lg">
              Opposite to Accent Ashlar Apartment, Chikkanagamangala,<br className="hidden sm:block" />
              2, Huskur Rd, Electronic City, Bengaluru, Karnataka 560099
            </p>
            <p className="text-[#00AEEF] font-semibold mb-8 text-lg">
              Click to get directions and plan your visit today!
            </p>
            <a
              href="https://www.google.com/maps/search/?api=1https://www.google.com/maps/search/?api=1&query=Aquarist+Pools+and+Fitness&query_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmMquery=Aquarist+Pools+and+Fitnesshttps://www.google.com/maps/search/?api=1&query=Aquarist+Pools+and+Fitness&query_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmMquery_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmM"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-get-directions-btn"
              className="inline-flex items-center gap-3 bg-[#00AEEF] hover:bg-[#0098d4] text-white font-bold text-lg px-10 py-5 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <MapPin className="w-6 h-6" />
              Get Directions (One Click)
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
